import { type ReactNode, useState } from "react";
import { useTranslations } from "use-intl";
import { Button, TextField } from "@mui/material";
import { get } from "lodash-es";
import {
  CaretDownIcon,
  AirplaneTakeoffIcon,
  AirplaneLandingIcon,
} from "@phosphor-icons/react";
import { FormikContext, useFormik } from "formik";
import { TripType, CabinClass, AirportType } from "@/app/lib/types/enums";
import { initialFlightsValues } from "@/app/lib/initialFlightsValues";
import AirportSearch from "./AirportSearch";
import TravellersSelection from "./TravellersSelection";
import DatesSelection from "./DatesSelection";
import dayjs from "dayjs";
import { rudderAnalytics, SearchTrackingEvents } from "@/app/lib/rudderstack";
import SwapValuesButton from "../SwapValuesButton";
import { type TFlightFormValues } from "@/app/lib/types/types";
import { Drawer, DrawerTopBar } from "../Drawer";
import { useSnackbarFormikErrors } from "@/app/lib/hooks/useSnackbarFormikErrors";
import { getFieldError } from "@/app/lib/validation";
import { getValidationSchema } from "./validation";
import { usePreviousFlightSearchData } from "@/app/lib/hooks/usePreviousSearchData";
import { getCityName } from "@/app/lib/utils/locations";
import TripTypeRadioGroup from "./TripTypeRadioGroup";
import { ResetButton } from "../ResetButton";
import { getDateString } from "@/app/lib/utils/dates";
import { DatesGuard } from "./DatesGuard";

enum DrawerComponent {
  AirportSearch = "AirportSearch",
  DatesSelection = "DatesSelection",
  PassengersSelection = "PassengersSelection",
}

const cabinClassLabels = {
  [CabinClass.ANY]: "anyClass",
  [CabinClass.ECONOMY]: "economy",
  [CabinClass.BUSINESS]: "business",
  [CabinClass.PREMIUM_ECONOMY]: "premiumEconomy",
  [CabinClass.FIRST_CLASS]: "firstClass",
};

type FlightFormFieldPath = NestedKeyOf<TFlightFormValues>;

type DrawerParams =
  | {
      component: DrawerComponent.AirportSearch;
      airportType: AirportType;
    }
  | {
      component: DrawerComponent.DatesSelection;
    }
  | {
      component: DrawerComponent.PassengersSelection;
    };

type Props = {
  submitButtonContent: ReactNode;
  initialValues?: TFlightFormValues | null;
  showErrorsInline?: boolean;
  onSubmit: (values: TFlightFormValues) => void;
  onAfterSubmit?: () => void;
  analyticsSource: "main" | "sticky";
};

export const FlightCalculator = ({
  submitButtonContent,
  initialValues,
  showErrorsInline,
  onSubmit,
  onAfterSubmit,
  analyticsSource,
}: Props) => {
  const [drawerParams, setDrawerParams] = useState<DrawerParams | null>(null);
  const { previousSearchData } = usePreviousFlightSearchData();
  const t = useTranslations();

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: getValidationSchema(t),
    validateOnChange: Boolean(showErrorsInline),
    validateOnBlur: Boolean(showErrorsInline),
    initialValues: initialValues || previousSearchData,
    onSubmit: (values) => {
      onSubmit(values);
      onAfterSubmit?.();
    },
  });

  useSnackbarFormikErrors<TFlightFormValues>({
    disabled: showErrorsInline,
    errors: formik.errors,
    touched: formik.touched,
  });

  const passengerCount =
    formik.values.adult + formik.values.child + formik.values.infantWithoutSeat;

  const changeAirportValues = () => {
    formik.setFieldValue("from", formik.values.to);
    formik.setFieldValue("to", formik.values.from);
  };

  const resetField = (
    e: React.MouseEvent<HTMLButtonElement>,
    type: FlightFormFieldPath,
  ) => {
    e.stopPropagation();
    formik.setFieldValue(type, get(initialFlightsValues, type));
  };

  const departureValue = dayjs(formik.values.dates.departure).format(
    "ddd, D MMM",
  );

  const returnValue = formik.values.dates.return
    ? dayjs(formik.values.dates.return).format("ddd, D MMM")
    : "_";

  return (
    <FormikContext.Provider value={formik}>
      <form
        className="w-full"
        noValidate
        onSubmit={formik.handleSubmit}
        acceptCharset="ISO-8859-1"
        aria-label={t("flights.search.flightSearchForm")}
      >
        <TripTypeRadioGroup
          value={formik.values.tripType}
          onChange={(e, value) => {
            const newTripType = value as TripType;

            rudderAnalytics.track(
              SearchTrackingEvents.USER_CHANGED_CALENDAR_DATE,
              {
                type: newTripType,
                dateFrom: getDateString(formik.values.dates.departure),
                ...(newTripType === TripType.ROUND_TRIP
                  ? {
                      dateTo: getDateString(formik.values.dates.return),
                    }
                  : {}),
                source: analyticsSource,
              },
            );

            formik.handleChange(e);
          }}
        />

        <div className="mb-4 flex flex-col gap-2">
          <div className="relative flex flex-col gap-[inherit]">
            <TextField
              fullWidth
              name="from.name"
              onClick={() => {
                setDrawerParams({
                  component: DrawerComponent.AirportSearch,
                  airportType: AirportType.FROM,
                });
              }}
              slotProps={{
                input: {
                  endAdornment: formik.values.from && (
                    <>
                      <ResetButton
                        aria-label={t("flights.search.resetDeparture")}
                        onClick={(e) => {
                          resetField(e, "from");
                        }}
                      />
                      {formik.values.from.type === "AIRPORT" && (
                        <span className="absolute end-10 text-sm text-text-placeholder">
                          {formik.values.from.code}
                        </span>
                      )}
                    </>
                  ),
                },
              }}
              sx={{
                input: {
                  paddingInlineEnd: "70px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  userSelect: "none",
                },
              }}
              value={getCityName(formik.values.from)}
              error={showErrorsInline && !!getFieldError(formik, "from.name")}
              helperText={
                showErrorsInline && getFieldError(formik, "from.name")
              }
              label={
                <div className="flex items-center gap-2">
                  <AirplaneTakeoffIcon size={24} />
                  <span>{t("flights.search.from")}</span>
                </div>
              }
            />
            <SwapValuesButton
              onClick={changeAirportValues}
              className="absolute end-5 top-1/2 z-10 -translate-y-1/2"
            />
            <TextField
              fullWidth
              value={getCityName(formik.values.to)}
              error={showErrorsInline && !!getFieldError(formik, "to.name")}
              helperText={showErrorsInline && getFieldError(formik, "to.name")}
              name="to.name"
              onClick={() => {
                setDrawerParams({
                  component: DrawerComponent.AirportSearch,
                  airportType: AirportType.TO,
                });
              }}
              slotProps={{
                input: {
                  endAdornment: formik.values.to && (
                    <>
                      <ResetButton
                        aria-label={t("flights.search.resetArrival")}
                        onClick={(e) => {
                          resetField(e, "to");
                        }}
                      />
                      {formik.values.to.type === "AIRPORT" && (
                        <span className="absolute end-10 text-sm text-text-placeholder">
                          {formik.values.to.code}
                        </span>
                      )}
                    </>
                  ),
                },
              }}
              label={
                <div className="flex items-center gap-2">
                  <AirplaneLandingIcon size={24} />
                  <span>{t("flights.search.to")}</span>
                </div>
              }
              sx={{
                input: {
                  paddingInlineEnd: "70px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  userSelect: "none",
                },
              }}
            />
          </div>
          <TextField
            onClick={() => {
              setDrawerParams({
                component: DrawerComponent.DatesSelection,
              });
            }}
            fullWidth
            label={t("flights.search.departureDate")}
            slotProps={{
              input: {
                endAdornment:
                  formik.values.dates.departure &&
                  !dayjs(formik.values.dates.departure).isSame(
                    initialFlightsValues.dates.departure,
                    "day",
                  ) ? (
                    <ResetButton
                      aria-label={t("flights.search.resetDates")}
                      onClick={(e) => {
                        resetField(e, "dates.departure");
                      }}
                    />
                  ) : null,
              },
            }}
            value={
              formik.values.tripType === TripType.ROUND_TRIP
                ? `${departureValue} - ${returnValue}`
                : departureValue
            }
          />
          <TextField
            fullWidth
            id="passengersNo"
            label={t("flights.search.travellers")}
            className="w-full p-0"
            slotProps={{
              input: {
                endAdornment: (
                  <CaretDownIcon
                    className="shrink-0"
                    color="black"
                    size={20}
                    weight="bold"
                  />
                ),
              },
            }}
            value={`${passengerCount > 1 ? t("persons") : t("person")} ${passengerCount}, ${t(
              `flights.cabinClass.${cabinClassLabels[formik.values.cabinClass]}`,
            )}`}
            onClick={() => {
              setDrawerParams({
                component: DrawerComponent.PassengersSelection,
              });
            }}
          />
        </div>

        <Button fullWidth variant="contained" type="submit">
          {submitButtonContent}
        </Button>
      </form>

      <DatesGuard />

      {/* we're not using open prop and must mount fresh instance of the component
        because otherwise initial opening animation breaks scroll when mobile keyboard is present */}
      {drawerParams ? (
        <Drawer
          hasFullHeight={!!drawerParams}
          onClose={() => setDrawerParams(null)}
          open
          disableRestoreFocus
        >
          {drawerParams.component === DrawerComponent.AirportSearch && (
            <>
              <DrawerTopBar
                onCloseClick={() => setDrawerParams(null)}
                title={t("flights.search.flightRoute")}
              />
              <AirportSearch
                formProps={formik}
                onDrawerClose={() => setDrawerParams(null)}
                initialFocusOn={drawerParams.airportType}
                analyticsSource={analyticsSource}
              />
            </>
          )}
          {drawerParams.component === DrawerComponent.DatesSelection && (
            <>
              <DrawerTopBar
                onCloseClick={() => setDrawerParams(null)}
                title={t("flights.search.departureDate")}
              />
              <DatesSelection
                initialValues={formik.values}
                onConfirm={(values) => {
                  rudderAnalytics.track(
                    SearchTrackingEvents.USER_CHANGED_CALENDAR_DATE,
                    {
                      type: values.tripType,
                      dateFrom: getDateString(values.dates.departure),
                      ...(values.tripType === TripType.ROUND_TRIP
                        ? {
                            dateTo: getDateString(values.dates.return),
                          }
                        : {}),
                      source: analyticsSource,
                    },
                  );
                  formik.setValues(values);
                  setDrawerParams(null);
                }}
              />
            </>
          )}
          {drawerParams.component === DrawerComponent.PassengersSelection && (
            <>
              <DrawerTopBar
                onCloseClick={() => setDrawerParams(null)}
                title={t("flights.search.travellersCabinClass")}
              />
              <TravellersSelection
                initialValues={formik.values}
                onConfirm={(values) => {
                  rudderAnalytics.track(
                    SearchTrackingEvents.USER_CHANGED_TRAVELLERS_AND_CLASS,
                    {
                      travellers: {
                        adults: values.adult,
                        children: values.child,
                        infants: values.infantWithoutSeat,
                      },
                      class: values.cabinClass,
                      source: analyticsSource,
                    },
                  );
                  formik.setValues(values);
                  setDrawerParams(null);
                }}
              />
            </>
          )}
        </Drawer>
      ) : null}
    </FormikContext.Provider>
  );
};
